import React from 'react';
import { Link } from 'react-router-dom';
import {
	ArrowRightIcon,
	CheckCircleIcon,
	HeartIcon,
	ShieldCheckIcon,
	SparklesIcon,
} from '@heroicons/react/24/outline';
import INSTALL from '../../assets/images/2026/gabe-selfie.png';
import HEADER from '../../assets/images/stock/panels.jpg';

// Why homeowners work with me — the credentials no other consultant can claim.
const credentials = [
	'Former solar installer',
	'Former crew leader & foreman',
	'Former electrician',
	'Project manager',
	'U.S. military veteran',
	'Hundreds of projects managed',
	'5+ years in solar',
	'Direct access throughout your project',
];

const values = [
	{
		name: 'People first',
		description:
			'Every roof is a real person, with a real bill, and real questions. I treat it that way.',
		icon: HeartIcon,
	},
	{
		name: 'Straight answers',
		description:
			'I tell you what makes sense for your home — even when the answer is "wait" or "not yet."',
		icon: ShieldCheckIcon,
	},
	{
		name: 'Long-term thinking',
		description:
			'Solar is a 25-year decision. Equipment, installer, and consultant all need to be around for the long run.',
		icon: SparklesIcon,
	},
];

function Eyebrow({ children, dark = false }) {
	return (
		<p
			className={`text-xs font-semibold uppercase tracking-[0.18em] ${
				dark ? 'text-primary-400' : 'text-primary-600'
			}`}
		>
			<span className='inline-block w-8 h-px align-middle mr-3 bg-current opacity-60' />
			{children}
		</p>
	);
}

function Header() {
	return (
		<section className='relative isolate overflow-hidden bg-ink-900'>
			<img
				src={HEADER}
				alt=''
				className='absolute inset-0 -z-10 h-full w-full object-cover opacity-35'
			/>
			<div className='absolute inset-0 -z-10 bg-gradient-to-br from-ink-900/85 via-ink-900/70 to-ink-800/55' />
			<div className='mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8'>
				<Eyebrow dark>About</Eyebrow>
				<h1 className='font-display mt-5 text-5xl font-medium tracking-tight text-white sm:text-6xl leading-[1.05]'>
					Hi, I&apos;m <span className='italic text-primary-400'>Gabe</span>.
				</h1>
				<p className='mt-6 max-w-2xl text-base leading-7 text-white/75'>
					I&apos;ve spent years on Maryland rooftops and around kitchen tables
					helping homeowners go solar. Here&apos;s why I do it — and what you
					can expect from me.
				</p>
			</div>
		</section>
	);
}

function Bio() {
	return (
		<section className='bg-surface py-24 sm:py-32'>
			<div className='mx-auto max-w-7xl px-6 lg:px-8'>
				<div className='grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center'>
					<div className='lg:col-span-5'>
						<div className='relative mx-auto max-w-sm'>
							<div className='absolute -inset-6 rounded-3xl bg-primary-100/70 blur-2xl' />
							<div className='relative rounded-3xl bg-white p-3 ring-1 ring-ink-100 shadow-xl shadow-ink-900/5'>
								<img
									src={INSTALL}
									alt='Solar panel installation on a rooftop'
									className='aspect-[4/5] w-full rounded-2xl object-cover'
								/>
							</div>
						</div>
					</div>
					<div className='lg:col-span-7'>
						<Eyebrow>Meet Gabriel</Eyebrow>
						<h2 className='font-display mt-4 text-4xl font-medium tracking-tight text-ink-900 sm:text-5xl leading-tight'>
							My journey started on the{' '}
							<span className='italic text-primary-600'>roof</span> — not in
							sales.
						</h2>
						<div className='mt-8 space-y-5 text-base leading-7 text-ink-500'>
							<p>
								Before becoming a consultant, I worked as a solar installer, crew
								leader, foreman, electrician, and project manager. That hands-on
								experience gives me a perspective that many solar consultants
								simply don&apos;t have.
							</p>
							<p>
								I&apos;ve spent years helping Maryland homeowners understand
								solar, battery storage, utility programs, warranties, and
								installation best practices.
							</p>
							<p>
								As a veteran and project manager, I believe in transparency,
								accountability, and doing things the right way.
							</p>
						</div>
						<div className='mt-8 rounded-2xl border-l-2 border-primary-500 bg-surface px-6 py-5'>
							<p className='text-sm font-semibold uppercase tracking-[0.18em] text-primary-600'>
								My mission is simple
							</p>
							<p className='mt-3 font-display text-xl font-medium text-ink-900'>
								Educate first. Serve the customer. Deliver a quality
								installation.
							</p>
							<p className='mt-3 text-sm leading-6 text-ink-500'>
								Whether you ultimately choose to work with me or not, I want you
								to have the information you need to make the best decision for
								your home and family.
							</p>
						</div>
						<div className='mt-10'>
							<Link
								to='/contact'
								className='inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-primary-500/20 hover:bg-primary-600 transition-all'
							>
								Get in touch
								<ArrowRightIcon className='h-4 w-4' />
							</Link>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}

function Values() {
	return (
		<section className='bg-white border-y border-ink-100 py-24 sm:py-32'>
			<div className='mx-auto max-w-7xl px-6 lg:px-8'>
				<div className='mx-auto max-w-2xl text-center'>
					<div className='flex justify-center'>
						<Eyebrow>How I work</Eyebrow>
					</div>
					<h2 className='font-display mt-4 text-4xl font-medium tracking-tight text-ink-900 sm:text-5xl leading-tight'>
						The principles behind every project.
					</h2>
				</div>
				<div className='mx-auto mt-16 grid max-w-4xl grid-cols-1 gap-6 sm:grid-cols-3'>
					{values.map((v) => (
						<div
							key={v.name}
							className='group sg-card p-8'
						>
							<div className='sg-badge'>
								<v.icon
									className='h-5 w-5'
									aria-hidden='true'
								/>
							</div>
							<h3 className='font-display mt-6 text-xl font-medium text-ink-900'>
								{v.name}
							</h3>
							<p className='mt-2 text-sm leading-6 text-ink-500'>
								{v.description}
							</p>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}

function Credentials() {
	return (
		<section className='relative overflow-hidden bg-ink-900 py-24 sm:py-32'>
			<div
				className='absolute inset-0 opacity-20'
				style={{
					backgroundImage:
						'radial-gradient(circle at 15% 15%, #3CB371 0%, transparent 45%), radial-gradient(circle at 85% 85%, #2A7E4F 0%, transparent 55%)',
				}}
			/>
			<div className='relative mx-auto max-w-7xl px-6 lg:px-8'>
				<div className='max-w-2xl'>
					<Eyebrow dark>Why homeowners work with me</Eyebrow>
					<h2 className='font-display mt-4 text-4xl font-medium tracking-tight text-white sm:text-5xl leading-tight'>
						Experience you can&apos;t fake.
					</h2>
					<p className='mt-6 text-base leading-7 text-white/70'>
						A solar system is expected to last 25–30 years. The person managing
						your project should understand it from the roof all the way through
						PTO and long-term ownership.
					</p>
				</div>
				<div className='mt-12 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-4'>
					{credentials.map((c) => (
						<div
							key={c}
							className='flex items-start gap-3'
						>
							<CheckCircleIcon className='mt-0.5 h-5 w-5 flex-none text-primary-400' />
							<span className='text-sm font-medium text-white/85'>{c}</span>
						</div>
					))}
				</div>
				<div className='mt-12'>
					<Link
						to='/why-choose-gabe'
						className='inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-primary-500/20 hover:bg-primary-600 transition-all'
					>
						Why homeowners choose me over the cheapest quote
						<ArrowRightIcon className='h-4 w-4' />
					</Link>
				</div>
			</div>
		</section>
	);
}

export default function About() {
	return (
		<div className='bg-surface text-ink-900 antialiased'>
			<Header />
			<Bio />
			<Credentials />
			<Values />
		</div>
	);
}
